import { apiWithoutAuth } from '@/shared/api/instances';

import { kimchiPremiumPaths } from './paths';

import type {
  KimchiPremiumChartPeriod,
  KimchiPremiumChartResponse,
  KimchiPremiumCurrentResult,
  KimchiPremiumDaily,
  KimchiPremiumMarketsResponse,
} from '../types';

export const findKimchiPremiumMarkets = async (
  exchange: string = 'UPBIT',
): Promise<KimchiPremiumMarketsResponse> => {
  const { data } = await apiWithoutAuth.get(kimchiPremiumPaths.markets, {
    params: { exchange },
  });
  return data.result;
};

/**
 * 김치프리미엄 현재가 + **신선도 메타** (TRAD-2281 / 2149).
 *
 * 🔴 종전엔 `data.result` 만 돌려주고 **`data.dataMeta` 를 버렸다.** 서버가 "이 데이터는
 * 언제 수집한 것이고 낡았다"고 말해줘도 클라가 그 말을 듣지 않으면, 화면은 낡은 값을
 * 최신인 양 보여준다 — `dataMeta` 를 추가한 목적 자체가 사라진다.
 *
 * dev 실응답(2026-08-04 실측):
 * `{"message":"<traceId>","result":[...],"dataMeta":{"collectedAt":"2026-08-04T13:10:26","stale":false}}`
 *
 * `dataMeta` 는 **선택 필드**다(하위호환을 위해 서버가 `@JsonInclude(NON_NULL)` 로 뺀다).
 * 없으면 `null` 로 정규화해 호출부가 옵셔널 체이닝 없이 다룰 수 있게 한다.
 */
export const findKimchiPremiumCurrent = async (
  exchange: string = 'UPBIT',
): Promise<KimchiPremiumCurrentResult> => {
  const { data } = await apiWithoutAuth.get(kimchiPremiumPaths.current, {
    params: { exchange },
  });
  return {
    items: data.result ?? [],
    dataMeta: data.dataMeta ?? null,
  };
};

export const findKimchiPremiumChart = async (
  coinCode: string,
  exchange: string = 'UPBIT',
  period: KimchiPremiumChartPeriod = '24H',
): Promise<KimchiPremiumChartResponse> => {
  const { data } = await apiWithoutAuth.get(kimchiPremiumPaths.chart(coinCode), {
    params: { exchange, period },
  });
  return data.result;
};

/**
 * TRAD-3709 P2 — 일간 요약(backend TRAD-3719). dev 실응답(2026-10-16 12:36): `{message, result:{exchange,date,periodStart,periodEnd,partial,asOf,
 * coinCount,marketAveragePremiumRate,pinned[],top[]}}` · 데이터 없는 날 404 `{code:"KIMCHI_PREMIUM_004"}` · 형식 오류 400 `E_002`.
 * 🔴 top 은 호출부가 바꾸지 않는다(서버 캐시 키) — 기본값 하나로 고정(lib/daily-page.ts KIMCHI_DAILY_TOP).
 */
export const findKimchiPremiumDaily = async (
  date: string,
  exchange: string = 'UPBIT',
  top: number = 20,
): Promise<KimchiPremiumDaily> => {
  const { data } = await apiWithoutAuth.get(kimchiPremiumPaths.daily(date), {
    params: { exchange, top },
  });
  return data.result;
};
